import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { isUndefined, omitBy } from "lodash-es";
import { defer, forkJoin, iif, Observable, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import {
  Recovery,
  RecoveryCreate,
  RecoveryLight
} from "src/app/backend/models/recovery";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import { BookBackend } from "src/app/backend/api/book.backend";
import { Book } from "src/app/backend/models/book/book";
import { PetBackend } from "src/app/backend/api/pet.backend";
import { PetLight } from "src/app/backend/models/pet/pet";
import { TicketBackend } from "src/app/backend/api/ticket.backend";
import { Ticket } from "src/app/backend/models/ticket";
import { omitUndefinedAndNullProperties } from "src/app/support/object";

@Injectable()
export class RecoveryBackend {
  private readonly http = inject(HttpClient);
  private readonly bookBackend = inject(BookBackend);
  private readonly patientBackend = inject(PetBackend);
  private readonly ticketBackend = inject(TicketBackend);

  list(request: PageRequest): Observable<PaginatedResult<Recovery>> {
    return this.http
      .get<PaginatedResult<Recovery>>(
        `${environment.snoots.api.healthcare}/recoveries`,
        {
          params: omitBy(
            {
              p: request.page,
              s: request.pageSize,
              ...request.filterValues
            },
            isUndefined
          )
        }
      )
      .pipe(
        switchMap((response) =>
          iif(
            () => response.docs?.length > 0,
            defer(() =>
              forkJoin(
                response.docs.map((recovery) =>
                  iif(
                    () => !!recovery.ticket,
                    defer(() =>
                      this.ticketBackend
                        .load({ uuid: recovery.ticket!.id! })
                        .pipe(
                          map((ticket) => ({
                            ...recovery,
                            ticket: ticket
                          })),
                          catchError(() =>
                            of({ ...recovery, ticket: undefined })
                          )
                        )
                    ),
                    of(recovery)
                  )
                )
              ).pipe(
                map((recoveries) => ({
                  ...response,
                  docs: recoveries
                }))
              )
            ),
            of(response)
          )
        )
      );
  }

  listWithPatient(request: PageRequest): Observable<PaginatedResult<Recovery>> {
    return this.list(request).pipe(
      switchMap((response) => {
        return iif(
          () => response.docs?.length > 0,
          defer(() =>
            forkJoin(
              response.docs.map((recovery) => {
                return iif(
                  () => !!recovery.patient,
                  defer(() =>
                    this.patientBackend.loadLight(recovery.patient.id!)
                  ),
                  of(undefined)
                ).pipe(
                  map((patient) => {
                    return {
                      ...recovery,
                      patient
                    };
                  })
                );
              })
            ).pipe(
              map((recoveries) => ({
                ...response,
                docs: <any>recoveries
              }))
            )
          ),
          of(response)
        );
      })
    );
  }

  listOpened(request: PageRequest): Observable<PaginatedResult<Recovery>> {
    return this.list({
      ...request,
      filterValues: {
        ...request.filterValues,
        opened: "true"
      }
    });
  }

  listOpenedExtended(
    request: PageRequest
  ): Observable<PaginatedResult<Recovery>> {
    return this.listOpened(request).pipe(
      switchMap((response) => {
        if (!response.docs || response.docs.length === 0) {
          return of(response);
        }

        return forkJoin(
          response.docs.map((recovery) => {
            return iif(
              () => !!recovery.patient && !!recovery.ticket,
              defer(() =>
                forkJoin({
                  book: this.bookBackend.loadSingle(recovery.patient.id),
                  patient: this.patientBackend.loadLight(recovery.patient.id)
                })
              ),
              of(undefined)
            ).pipe(
              map(
                (
                  result:
                    | { book: Book | undefined; patient: PetLight }
                    | undefined
                ) => {
                  const bookItemsTotal = result.book?.items
                    ?.filter((i) => i.type !== "advance-payment")
                    .reduce((accumulator, currentValue) => {
                      if (
                        currentValue.invoice &&
                        currentValue.invoice.number &&
                        currentValue.invoice.emittedAt &&
                        currentValue.invoice.emittedBy
                      ) {
                        return accumulator;
                      }
                      return accumulator + currentValue.adjustedTotal;
                    }, 0);
                  return {
                    ...recovery,
                    total: bookItemsTotal,
                    patient: result?.patient || recovery.patient
                  };
                }
              )
            );
          })
        ).pipe(
          map((recoveriesExtended) => ({
            ...response,
            docs: recoveriesExtended
          }))
        );
      })
    );
  }

  listAllOpened(): Observable<Recovery[]> {
    return this.list({
      page: 1,
      pageSize: 10000,
      filterValues: {
        opened: "true"
      }
    }).pipe(map((response) => response.docs));
  }

  listAllOpenedExtended({
    createdBy
  }: {
    createdBy?: string[];
  }): Observable<Recovery[]> {
    return this.listOpenedExtended({
      page: 1,
      pageSize: 10000,
      filterValues: omitUndefinedAndNullProperties({
        createdBy:
          createdBy && createdBy.length > 0 ? createdBy.join(",") : undefined
      })
    }).pipe(map((response) => response.docs));
  }

  load(id: string): Observable<Recovery> {
    return this.http
      .get<Recovery>(`${environment.snoots.api.healthcare}/recoveries/${id}`)
      .pipe(
        switchMap((recovery) =>
          iif(
            () => !!recovery.ticket,
            defer(() =>
              this.ticketBackend
                .load({ uuid: recovery.ticket!.id! })
                .pipe(map((ticket) => ({ ...recovery, ticket })))
            ),
            of(recovery)
          )
        )
      );
  }

  loadLight(id: string): Observable<RecoveryLight> {
    return this.http.get<Recovery>(
      `${environment.snoots.api.healthcare}/recoveries/${id}`,
      {
        params: {
          light: true
        }
      }
    );
  }

  getActiveRecovery({
    patientId,
    ticketId
  }: {
    patientId?: string;
    ticketId: string;
  }): Observable<Recovery | undefined> {
    return iif(
      () => !!patientId,
      defer(() =>
        this.listOpened({
          page: 1,
          pageSize: 1,
          filterValues: {
            ticketId: ticketId,
            patientId: patientId,
            light: true
          }
        }).pipe(map(({ docs }) => docs?.[0]))
      ),
      of(undefined)
    );
  }

  @UserNotify({
    successMessage: marker("Degenza creata"),
    errorMessage: marker("Impossibile creare la Degenza")
  })
  create(patientId: string, payload: RecoveryCreate): Observable<Recovery> {
    return this.createSilenlty(patientId, payload);
  }

  @UserNotify({
    successMessage: marker("Degenza creata"),
    errorMessage: marker("Impossibile creare la Degenza")
  })
  createAndTicketInProgress(
    patientId: string,
    payload: RecoveryCreate
  ): Observable<Recovery> {
    return this.createSilenlty(patientId, payload).pipe(
      switchMap((recovery) => {
        return this.ticketBackend
          .openSilently(payload.ticket.id)
          .pipe(map(() => recovery));
      })
    );
  }

  private createSilenlty(
    patientId: string,
    payload: RecoveryCreate
  ): Observable<Recovery> {
    return this.http.post<Recovery>(
      `${environment.snoots.api.healthcare}/patients/${patientId}/recoveries`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Degenza aggiornata"),
    errorMessage: marker("Impossibile aggiornare la degenza")
  })
  update(id: string, request: Partial<Recovery>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/recoveries/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Dimissione effettuata"),
    errorMessage: marker("Impossibile effettuare la dimissione")
  })
  complete(recovery: RecoveryLight): Observable<void> {
    return iif(
      () => !!recovery.ticket,
      defer(() =>
        this.ticketBackend
          .completeSilently({ id: recovery.ticket!.id! })
          .pipe(catchError(() => of(undefined)))
      ),
      of(undefined)
    ).pipe(
      switchMap(() => {
        return this.http.put<void>(
          `${environment.snoots.api.healthcare}/recoveries/${recovery.id}/complete`,
          {}
        );
      })
    );
  }

  @UserNotify({
    successMessage: marker("Degenza eliminata"),
    errorMessage: marker("Impossibile eliminare la degenza")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.healthcare}/recoveries/${id}`
    );
  }

  loadOpenRecoveryForPatient(
    patientId: string
  ): Observable<Recovery | undefined> {
    return this.http
      .get<PaginatedResult<Recovery>>(
        `${environment.snoots.api.healthcare}/recoveries`,
        {
          params: {
            patientId,
            open: "true"
          }
        }
      )
      .pipe(
        map((response) => response.docs?.find((x) => !x.completedAt)),
        switchMap((recovery: Recovery | undefined) =>
          iif(
            () => !!recovery && !!recovery!.ticket,
            defer(() =>
              this.ticketBackend
                .load({ uuid: recovery!.ticket!.id! })
                .pipe(map((ticket: Ticket) => ({ ...recovery!, ticket })))
            ),
            of(recovery)
          )
        )
      );
  }

  listForPatient({
    patientId,
    p,
    s
  }: {
    patientId: string;
    p: string;
    s: string;
  }): Observable<PaginatedResult<Recovery>> {
    return this.list({
      page: parseInt(p),
      pageSize: parseInt(s),
      filterValues: {
        patientId
      }
    });
  }
}
